/**
 * Token processor for handling dynamic tokens in override values
 * Tokens are in the format <user.field> and get replaced with actual user data
 */

/**
 * Extract tokens from a string
 * @param {string} value - The string to extract tokens from
 * @returns {string[]} Array of tokens found
 */
export const extractTokens = (value) => {
  if (!value || typeof value !== 'string') return [];
  // Match both <user.field> and <field> formats
  const tokenRegex = /<(?:user\.)?(\w+)>/g;
  const tokens = [];
  let match;
  while ((match = tokenRegex.exec(value)) !== null) {
    tokens.push(match[0]);
  }
  return tokens;
};

/**
 * Replace tokens in a value with user data
 * @param {string} value - The value containing tokens
 * @param {Object} userData - The user data object
 * @returns {string} The value with tokens replaced
 */
export const replaceTokens = (value, userData) => {
  if (!value || typeof value !== 'string' || !userData) return value;
  
  // Replace both <user.field> and <field> formats
  return value.replace(/<(?:user\.)?(\w+)>/g, (match, field) => {
    // Check if field exists in userData
    const result = userData[field];
    
    // Convert result to string if it's not null/undefined, otherwise return empty string
    return result !== null && result !== undefined ? String(result) : '';
  });
};

/**
 * Check if a value contains tokens
 * @param {string} value - The value to check
 * @returns {boolean} True if value contains tokens
 */
export const hasTokens = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /<(?:user\.)?(\w+)>/.test(value);
};

/**
 * Get all unique user fields needed from a set of overrides
 * @param {Array} overrides - Array of override rules
 * @returns {Set<string>} Set of unique field names needed
 */
export const getRequiredUserFields = (overrides) => {
  const fields = new Set();
  
  overrides.forEach(override => {
    // Check condition values
    override.conditions?.forEach(condition => {
      const tokens = extractTokens(condition.value);
      tokens.forEach(token => {
        const field = token.match(/<(?:user\.)?(\w+)>/)?.[1];
        if (field) fields.add(field);
      });
    });
    
    // Check override values
    Object.values(override.override || {}).forEach(value => {
      const tokens = extractTokens(value);
      tokens.forEach(token => {
        const field = token.match(/<(?:user\.)?(\w+)>/)?.[1];
        if (field) fields.add(field);
      });
    });
  });
  
  return fields;
};

/**
 * Process overrides to replace tokens in conditions and override values
 * @param {Array} overrides - Array of override rules
 * @param {Object} userData - User data for token replacement
 * @returns {Array} Processed overrides with tokens replaced
 */
export const processOverridesWithTokens = (overrides, userData) => {
  if (!overrides || !userData) return overrides;
  
  return overrides.map(override => ({
    conditions: override.conditions.map(condition => ({
      ...condition,
      value: replaceTokens(condition.value, userData)
    })),
    override: Object.entries(override.override).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: replaceTokens(value, userData)
    }), {})
  }));
};